export function getCandidatesTableLayout(screeningMeta, options = {}) {
  const showShortlistedDate = options?.showShortlistedDate ?? true;
  const showRejectedDate = options?.showRejectedDate ?? true;
  const showStatus = options?.showStatus ?? false;
  const showAssessment = options?.showAssessment ?? (screeningMeta?.hasAssessment ?? true);
  const columnVisibility = options?.columnVisibility || {};

  const resolveColumnVisibility = (key, defaultValue) => {
    if (Object.prototype.hasOwnProperty.call(columnVisibility, key)) {
      return columnVisibility[key] === true;
    }
    return defaultValue;
  };

  // Single-method UI layouts (exactly one method selected at screening creation time).
  const isResumeOnly =
    screeningMeta?.hasResumeScoring !== false &&
    screeningMeta?.includeAssessment === false &&
    screeningMeta?.includeInterview === false;

  const isAssessmentOnly =
    screeningMeta?.hasResumeScoring === false &&
    screeningMeta?.includeAssessment !== false &&
    screeningMeta?.includeInterview === false;

  const isInterviewOnly =
    screeningMeta?.hasResumeScoring === false &&
    screeningMeta?.includeAssessment === false &&
    screeningMeta?.includeInterview !== false;

  // Exactly two methods enabled at screening creation (resume + assessment, resume + interview, or assessment + interview).
  const isAssessmentInterviewDual =
    screeningMeta?.hasResumeScoring === false &&
    screeningMeta?.includeAssessment !== false &&
    screeningMeta?.includeInterview !== false;

  const isResumeAssessmentDual =
    screeningMeta?.hasResumeScoring !== false &&
    screeningMeta?.includeAssessment !== false &&
    screeningMeta?.includeInterview === false;

  const isResumeInterviewDual =
    screeningMeta?.hasResumeScoring !== false &&
    screeningMeta?.includeAssessment === false &&
    screeningMeta?.includeInterview !== false;

  const isDualMethodLayout = isAssessmentInterviewDual || isResumeAssessmentDual || isResumeInterviewDual;

  // Resume scoring + online assessment + AI interview (all three selected at creation).
  const isAllThreeMethods =
    screeningMeta?.hasResumeScoring !== false &&
    screeningMeta?.includeAssessment !== false &&
    screeningMeta?.includeInterview !== false;

  const isSimplifiedSingleMode = isResumeOnly || isAssessmentOnly || isInterviewOnly;

  // No AI scoring methods enabled (e.g. Document Collection ONLY screening)
  const isNoScoringMethods =
    screeningMeta?.hasResumeScoring === false &&
    (screeningMeta?.includeAssessment === false || (!screeningMeta?.assessmentId && !screeningMeta?.hasAssessment)) &&
    (screeningMeta?.includeInterview === false ||
      (!screeningMeta?.interviewId && !screeningMeta?.hasInterview && !screeningMeta?.includeWhatsAppAgent)) &&
    !screeningMeta?.isMovedCandidatesPool;

  const showOverallScoreColumn = resolveColumnVisibility(
    'overallScore',
    !isSimplifiedSingleMode && !isNoScoringMethods
  );

  const showResumeScoreColumn =
    resolveColumnVisibility(
      'resumeScore',
      !isNoScoringMethods &&
        (isResumeOnly ||
          isResumeAssessmentDual ||
          isResumeInterviewDual ||
          (!isSimplifiedSingleMode && !isAssessmentInterviewDual))
    );

  const showAssessmentScoreColumn =
    resolveColumnVisibility(
      'assessmentScore',
      !isNoScoringMethods &&
        (isAssessmentOnly ||
          isResumeAssessmentDual ||
          isAssessmentInterviewDual ||
          (!isSimplifiedSingleMode && !isResumeInterviewDual && showAssessment))
    );

  const showInterviewScoreColumn =
    resolveColumnVisibility(
      'interviewScore',
      !isNoScoringMethods &&
        (isInterviewOnly ||
          isResumeInterviewDual ||
          isAssessmentInterviewDual ||
          (!isSimplifiedSingleMode && !isResumeAssessmentDual))
    );

  const isShortlistView = options?.isShortlistView ?? false;

  const showProctoringColumn =
    resolveColumnVisibility(
      'proctoring',
      isShortlistView 
        ? false 
        : (!isSimplifiedSingleMode && !isNoScoringMethods ? true : (isAssessmentOnly || isInterviewOnly || isDualMethodLayout) && !isNoScoringMethods)
    );

  const showCompanyNameColumn = resolveColumnVisibility('companyName', false);

  // Store List orgs get the store's location columns ON by default. Without this they
  // were all hidden, so a recruiter in a store-based org saw a Store Code with no way
  // to tell which store it was until they hunted through the Columns modal. Category /
  // ARM Name / ARM Email stay off by default to keep the table narrow — they're still
  // one click away in Columns. Callers signal the flag either explicitly
  // (`storeListEnabled`) or by passing the auth `user` object through.
  // NOTE: an org that has explicitly saved a column layout still wins — resolveColumnVisibility
  // only falls through to these defaults when the org has no stored preference for that key.
  const storeListEnabled =
    options?.storeListEnabled === true || options?.user?.features?.store_list === true;

  const showStoreNameColumn = resolveColumnVisibility('storeName', storeListEnabled);
  const showStoreRegionColumn = resolveColumnVisibility('storeRegion', storeListEnabled);
  const showStoreCityColumn = resolveColumnVisibility('storeCity', storeListEnabled);
  const showStoreStateColumn = resolveColumnVisibility('storeState', storeListEnabled);
  const showStoreCategoryColumn = resolveColumnVisibility('storeCategory', false);
  const showArmNameColumn = resolveColumnVisibility('armName', false);
  const showArmEmailColumn = resolveColumnVisibility('armEmail', false);
  const showCityCityColumn = resolveColumnVisibility('cityCity', false);
  const showCityStateColumn = resolveColumnVisibility('cityState', false);
  const showCityRegionColumn = resolveColumnVisibility('cityRegion', false);
  const showScreeningColumn = resolveColumnVisibility('screening', false);
  const showCurrentCtcColumn = resolveColumnVisibility('currentCtc', false);
  const showNoticePeriodColumn = resolveColumnVisibility('noticePeriod', false);
  const showAgeColumn = resolveColumnVisibility('age', false);
  const showGenderColumn = resolveColumnVisibility('gender', false);
  const showExperienceColumn = resolveColumnVisibility('experience', false);
  const showLatestEducationColumn = resolveColumnVisibility('latestEducation', false);
  const showDesignationColumn = resolveColumnVisibility('designation', false);
  const showLocationColumn = resolveColumnVisibility('location', false);
  const showIndustryColumn = resolveColumnVisibility('industry', false);
  const showSkillsColumn = resolveColumnVisibility('skills', false);
  const showExperienceYearsColumn = resolveColumnVisibility('experienceYears', false);
  const showEmailColumn = resolveColumnVisibility('email', false);
  const showPhoneColumn = resolveColumnVisibility('phone', true);
  const showSocialColumn = resolveColumnVisibility('social', false);
  const showAppliedDateColumn = resolveColumnVisibility('appliedDate', false);

  const showShortlistedDateEffective = resolveColumnVisibility('shortlistedDate', showShortlistedDate);
  const showRejectedDateEffective = resolveColumnVisibility('rejectedDate', showRejectedDate);
  const showSalaryDetailsColumn = resolveColumnVisibility('salaryDetails', options?.hasArmSalaryDetails === true);
  const showCompletedAtColumn = resolveColumnVisibility('completedAt', true);
  const showCandidateDetailsColumn = resolveColumnVisibility('candidateDetails', false);
  // On by default for any screening that runs a qualifying stage — it is the only place the
  // call/WhatsApp conversation is visible, so hiding it by default would hide the stage.
  const showQualifyingCallColumn = resolveColumnVisibility(
    'qualifyingCall',
    screeningMeta?.includeQualifyingAgent === true
  );

  const showCandidateMetaColumns =
    isSimplifiedSingleMode ||
    showScreeningColumn ||
    showCompanyNameColumn ||
    showCurrentCtcColumn ||
    showNoticePeriodColumn ||
    showAgeColumn ||
    showGenderColumn ||
    showExperienceColumn ||
    showLatestEducationColumn ||
    showDesignationColumn ||
    showLocationColumn ||
    showIndustryColumn ||
    showSkillsColumn ||
    showExperienceYearsColumn ||
    showStoreNameColumn ||
    showStoreRegionColumn ||
    showStoreCityColumn ||
    showStoreStateColumn ||
    showStoreCategoryColumn ||
    showArmNameColumn ||
    showArmEmailColumn ||
    showCityCityColumn ||
    showCityStateColumn ||
    showCityRegionColumn ||
    showEmailColumn ||
    showPhoneColumn ||
    showSocialColumn ||
    showAppliedDateColumn;

  const showMetaColumnsBlock = showCandidateMetaColumns;

  const showResumeInterviewExtraColumns =
    !showCandidateMetaColumns &&
    resolveColumnVisibility('resumeInterviewExtraColumns', isResumeInterviewDual);
  const showResumeAssessmentExtraColumns =
    !showCandidateMetaColumns &&
    resolveColumnVisibility('resumeAssessmentExtraColumns', isResumeAssessmentDual);
  const showResumeAssessmentExperienceColumn =
    !showCandidateMetaColumns &&
    showResumeAssessmentExtraColumns &&
    resolveColumnVisibility('resumeAssessmentExperience', false);
  const showResumeInterviewExperienceColumn =
    !showCandidateMetaColumns &&
    showResumeInterviewExtraColumns &&
    resolveColumnVisibility('resumeInterviewExperience', false);

  return {
    // layout modes
    isResumeOnly,
    isAssessmentOnly,
    isInterviewOnly,
    isAssessmentInterviewDual,
    isResumeAssessmentDual,
    isResumeInterviewDual,
    isDualMethodLayout,
    isAllThreeMethods,
    isSimplifiedSingleMode,

    // columns
    showOverallScoreColumn,
    showResumeScoreColumn,
    showAssessmentScoreColumn,
    showInterviewScoreColumn,
    showShortlistedDateEffective,
    showRejectedDateEffective,
    showProctoringColumn,
    showCandidateMetaColumns,
    showMetaColumnsBlock,
    showScreeningColumn,
    showCompanyNameColumn,
    showStoreNameColumn,
    showStoreRegionColumn,
    showStoreCityColumn,
    showStoreStateColumn,
    showStoreCategoryColumn,
    showArmNameColumn,
    showArmEmailColumn,
    showCityCityColumn,
    showCityStateColumn,
    showCityRegionColumn,
    showCurrentCtcColumn,
    showNoticePeriodColumn,
    showAgeColumn,
    showGenderColumn,
    showExperienceColumn,
    showLatestEducationColumn,
    showDesignationColumn,
    showLocationColumn,
    showIndustryColumn,
    showSkillsColumn,
    showExperienceYearsColumn,
    showEmailColumn,
    showPhoneColumn,
    showSocialColumn,
    showAppliedDateColumn,
    showResumeInterviewExtraColumns,
    showResumeAssessmentExtraColumns,
    showResumeAssessmentExperienceColumn,
    showResumeInterviewExperienceColumn,
    showCompletedAtColumn,
    showSalaryDetailsColumn,
    showCandidateDetailsColumn,
    showQualifyingCallColumn,
  };
}

